import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useMap } from '@vis.gl/react-google-maps';
import debounce from 'lodash/debounce';

interface PlacePrediction {
  description: string;
  place_id: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
}

const SearchBox: FC = () => {
  const map = useMap();
  const [searchText, setSearchText] = useState('');
  const [predictions, setPredictions] = useState<PlacePrediction[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const searchBoxRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const placesServiceRef = useRef<google.maps.places.PlacesService>();
  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken>();
  const suggestionsCache = useRef<Record<string, google.maps.places.AutocompletePrediction[]>>({});

  useEffect(() => {
    if (!map) return;

    // Initialize Places Service
    placesServiceRef.current = new google.maps.places.PlacesService(map);
    sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken();

    // Position the search box on the map
    if (searchBoxRef.current) {
      map.controls[google.maps.ControlPosition.TOP_LEFT].push(searchBoxRef.current);
    }

    return () => {
      // Remove the search box from the map controls when component unmounts
      if (searchBoxRef.current) {
        const index = map.controls[google.maps.ControlPosition.TOP_LEFT].getArray().indexOf(searchBoxRef.current);
        if (index !== -1) {
          map.controls[google.maps.ControlPosition.TOP_LEFT].removeAt(index);
        }
      }
    };
  }, [map]);

  const debouncedPredictions = useRef(
    debounce(
      (
        input: string,
        bounds: google.maps.LatLngBounds | undefined,
        callback: (predictions: google.maps.places.AutocompletePrediction[]) => void
      ) => {
        if (suggestionsCache.current[input]) {
          callback(suggestionsCache.current[input]);
          return;
        }

        const service = new google.maps.places.AutocompleteService();
        service.getPlacePredictions(
          {
            input,
            sessionToken: sessionTokenRef.current,
            locationBias: bounds || undefined
          },
          (predictionResults, status) => {
            if (status !== google.maps.places.PlacesServiceStatus.OK || !predictionResults) {
              callback([]);
              return;
            }
            suggestionsCache.current[input] = predictionResults;
            callback(predictionResults);
          }
        );
      },
      600
    )
  ).current;

  // Handle search input changes
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchText(value);

      if (!value.trim()) {
        setPredictions([]);
        setIsDropdownOpen(false);
        return;
      }

      // Create a new session token for each new search session
      if (!sessionTokenRef.current) {
        sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken();
      }

      const bounds = map?.getBounds();

      // Check cache for existing predictions that match the input
      const cachedPrefix = Object.keys(suggestionsCache.current).find(
        key => value.startsWith(key) && suggestionsCache.current[key]?.length > 0
      );

      if (cachedPrefix) {
        const filteredSuggestions = suggestionsCache.current[cachedPrefix].filter(
          suggestion => suggestion.description.toLowerCase().includes(value.toLowerCase())
          // eslint-disable-next-line function-paren-newline
        );

        if (filteredSuggestions.length > 0) {
          setPredictions(filteredSuggestions);
          setIsDropdownOpen(true);
          return;
        }
      }

      debouncedPredictions(value, bounds, predictionResults => {
        setPredictions(predictionResults);
        setIsDropdownOpen(predictionResults.length > 0);
      });
    },
    [map]
  );

  // Handle place selection
  const handlePlaceSelect = useCallback(
    (prediction: PlacePrediction) => {
      if (!map || !placesServiceRef.current) return;

      placesServiceRef.current.getDetails(
        {
          placeId: prediction.place_id,
          fields: ['geometry', 'formatted_address'],
          sessionToken: sessionTokenRef.current
        },
        (place, status) => {
          if (status !== google.maps.places.PlacesServiceStatus.OK || !place || !place.geometry) return;

          // Update the search input with the selected place
          setSearchText(prediction.description);
          setIsDropdownOpen(false);
          setPredictions([]);

          // Create a new session token for the next search
          sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken();

          // Update map view
          if (place.geometry.viewport) {
            map.fitBounds(place.geometry.viewport);
          } else if (place.geometry.location) {
            map.setCenter(place.geometry.location);
            map.setZoom(17);
          }
        }
      );
    },
    [map]
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={searchBoxRef} className="relative m-2">
      <input
        ref={inputRef}
        type="text"
        value={searchText}
        onChange={handleSearchChange}
        placeholder="Search map"
        className="h-10 w-[300px] rounded-md border border-borderGray
         bg-white px-4 text-[15px] font-medium shadow-sm outline-none focus:border-secondary"
      />

      {/* Custom dropdown for place suggestions */}
      {isDropdownOpen && predictions.length > 0 && (
        <div
          className="absolute inset-x-0 top-full mt-1 max-h-[300px] overflow-y-auto rounded-md border 
            border-borderGray bg-white shadow-lg">
          {predictions.map(prediction => (
            <button
              key={prediction.place_id}
              onClick={() => handlePlaceSelect(prediction)}
              className="flex w-full flex-col px-4 py-2 text-left hover:bg-gray-100">
              <span className="font-medium">{prediction.structured_formatting.main_text}</span>
              <span className="text-sm text-gray-600">{prediction.structured_formatting.secondary_text}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default SearchBox;
